import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { FieldArray, Formik, FormikErrors } from 'formik';
import * as yup from 'yup';
import { IonIcon } from '@ionic/react';
import { openOutline } from 'ionicons/icons';
import AccountHeader from '../components/AccountHeader';
import ErrorToast from '../components/ErrorToast';
import InfoToast from '../components/InfoToast';
import Loader from '../components/Loader';
import StorefrontCollectionCard from '../components/StorefrontCollectionCard';
import DeleteStorefrontModal from '../components/DeleteStorefrontModal';
import ConfirmCollectionRemovalModal from '../components/ConfirmCollectionRemovalModal';
import { useAuthStore } from '../store/zustand';
import {
  IStorefrontCollectionFormValue,
  IStorefrontFormValues,
} from '../interfaces/storefront-form.interface';
import { IStorefront } from '../interfaces/storefront.interface';
import {
  IStorefrontCollectionInput,
  IStorefrontUpdateInput,
  StorefrontService,
} from '../services/StorefrontService';
import { StorefrontCollectionLayout } from '../enums/storefront-collection-layout.enum';
import { StorefrontError } from '../enums/storefront-error.enum';
import {
  UUID_REGEX,
  createEmptyCollection,
  getStorefrontErrorCode,
  getStorefrontErrorMessage,
  toCollectionFormValues,
  toCollectionInputs,
  toProductNames,
} from '../utils/storefront';
import {
  SHOP_URL,
  STOREFRONT_COLLECTIONS_MAX,
  STOREFRONT_COLLECTION_DESCRIPTION_MAX_LENGTH,
  STOREFRONT_COLLECTION_NAME_MAX_LENGTH,
  STOREFRONT_COLLECTION_PRODUCTS_MAX,
  STOREFRONT_DESCRIPTION_MAX_LENGTH,
  STOREFRONT_TAGLINE_MAX_LENGTH,
} from '../constants';

const DELETE_MODAL_ID = 'delete-storefront';
const REMOVAL_MODAL_ID = 'confirm-collection-removal';

const hideModal = (targetId: string) => {
  // @ts-ignore
  if (typeof window?.UIkit !== 'undefined') {
    // @ts-ignore
    UIkit.modal(`#${targetId}`).hide();
  }
};

const showModal = (targetId: string) => {
  // @ts-ignore
  if (typeof window?.UIkit !== 'undefined') {
    // @ts-ignore
    UIkit.modal(`#${targetId}`).show();
  }
};

const Storefront = () => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const user = useAuthStore((state) => state.user);

  // The server response is mirrored here so a background refetch can not reset
  // the form while collections are being edited.
  const [storefront, setStorefront] = useState<IStorefront>();
  const [pendingUpdate, setPendingUpdate] = useState<IStorefrontUpdateInput>();
  const [pendingRemovalCount, setPendingRemovalCount] = useState(0);

  const getStorefrontQuery = useQuery({
    queryKey: ['storefront'],
    queryFn: async () => {
      return await StorefrontService.getUserStorefront();
    },
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: async (data) => {
      setStorefront(data);
    },
    onError: async (e: any) => {
      // A 404 just means the partner has no storefront yet.
      if (e?.response?.status !== 404) {
        ErrorToast(t('Errors.general'));
      }
    },
  });

  const createStorefrontMutation = useMutation(
    (createData: {
      tagline: string;
      description: string;
      collections?: IStorefrontCollectionInput[];
    }) => {
      return StorefrontService.createUserStorefront(createData);
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['storefront'], data);
        setStorefront(data);

        InfoToast(t('Storefront.created'));
      },
      onError: (e: any) => {
        if (
          getStorefrontErrorCode(e) ===
          StorefrontError.STOREFRONT_ALREADY_EXISTS
        ) {
          void queryClient.invalidateQueries(['storefront']);
        }

        ErrorToast(getStorefrontErrorMessage(e, t));
      },
    },
  );

  const updateStorefrontMutation = useMutation(
    (updateData: IStorefrontUpdateInput) => {
      return StorefrontService.updateUserStorefront(updateData);
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['storefront'], data);
        setStorefront(data);

        hideModal(REMOVAL_MODAL_ID);
        setPendingUpdate(undefined);

        InfoToast(t('Storefront.updated'));
      },
      onError: (e: any) => {
        hideModal(REMOVAL_MODAL_ID);
        setPendingUpdate(undefined);

        if (
          getStorefrontErrorCode(e) === StorefrontError.STOREFRONT_NOT_FOUND
        ) {
          queryClient.removeQueries(['storefront']);
          setStorefront(undefined);
        }

        ErrorToast(getStorefrontErrorMessage(e, t));
      },
    },
  );

  const deleteStorefrontMutation = useMutation(
    () => {
      return StorefrontService.deleteUserStorefront();
    },
    {
      onSuccess: () => {
        queryClient.removeQueries(['storefront']);
        setStorefront(undefined);

        hideModal(DELETE_MODAL_ID);

        InfoToast(t('Storefront.deleted'));
      },
      onError: (e: any) => {
        hideModal(DELETE_MODAL_ID);

        if (
          getStorefrontErrorCode(e) === StorefrontError.STOREFRONT_NOT_FOUND
        ) {
          queryClient.removeQueries(['storefront']);
          setStorefront(undefined);
        }

        ErrorToast(getStorefrontErrorMessage(e, t));
      },
    },
  );

  const hasNoStorefront =
    getStorefrontQuery.isError &&
    (getStorefrontQuery.error as any)?.response?.status === 404;

  const initialValues: IStorefrontFormValues = {
    tagline: storefront?.tagline ?? '',
    description: storefront?.description ?? '',
    collections: toCollectionFormValues(storefront?.collections ?? []),
  };

  // Only the api knows a product's name, so an id added since the last save is
  // missing here until the storefront comes back from a mutation.
  const productNames = toProductNames(storefront?.collections ?? []);

  const submitForm = (values: IStorefrontFormValues) => {
    // A save already in flight owns the form's state, a second one on top
    // could interleave with it and report a stale error or success.
    if (
      createStorefrontMutation.isLoading ||
      updateStorefrontMutation.isLoading
    ) {
      return;
    }

    const collections = toCollectionInputs(values.collections);

    // Nothing exists server side yet, so there is no diff to take and no
    // collection that could be removed. The api writes the storefront and its
    // collections in one transaction, so a rejected create leaves nothing
    // behind and the form keeps everything that was typed.
    if (!storefront) {
      createStorefrontMutation.mutate({
        tagline: values.tagline,
        description: values.description,
        collections: collections.length === 0 ? undefined : collections,
      });

      return;
    }

    const initialCollections = toCollectionInputs(initialValues.collections);

    const collectionsChanged =
      JSON.stringify(collections) !== JSON.stringify(initialCollections);

    // Collections are a full replace on the api, so they are left out entirely
    // unless they actually changed.
    const update: IStorefrontUpdateInput = {
      tagline: values.tagline,
      description: values.description,
      collections: collectionsChanged ? collections : undefined,
    };

    const removedCount = initialCollections.filter(
      (initial) =>
        !!initial.id &&
        !collections.some((collection) => collection.id === initial.id),
    ).length;

    if (removedCount !== 0) {
      setPendingUpdate(update);
      setPendingRemovalCount(removedCount);

      showModal(REMOVAL_MODAL_ID);
    } else {
      updateStorefrontMutation.mutate(update);
    }
  };

  return (
    <Layout>
      <div className="max-w-[1065px] mx-auto pb-5 sm:pb-10">
        {(user === undefined ||
          getStorefrontQuery.isLoading ||
          createStorefrontMutation.isLoading ||
          updateStorefrontMutation.isLoading ||
          deleteStorefrontMutation.isLoading) && <Loader />}

        {user && (
          <>
            <AccountHeader user={user} />

            <div
              className="flex 2xl:gap-12 gap-6 mt-6 max-lg:flex-col"
              id="js-oversized"
            ></div>

            <div className="flex-1">
              <div className="sm:mb-6 mb-3 flex items-center justify-between gap-4">
                <div>
                  <h2 className="md:text-lg text-base font-semibold text-black">
                    {t('Storefront.title')}
                  </h2>
                  <p className="font-normal text-sm text-gray-500 leading-6">
                    {t('Storefront.subtitle')}
                  </p>
                </div>

                {storefront && (
                  <button
                    type="button"
                    className="button py-2 px-3 sm:py-2 sm:px-5 border border-red-500 text-red-500 shrink-0"
                    data-uk-toggle={`target: #${DELETE_MODAL_ID}`}
                  >
                    {t('Storefront.delete')}
                  </button>
                )}
              </div>

              {storefront ? (
                <div className="box p-5 dark:bg-white/5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-semibold">
                        {t('Storefront.name')}
                      </h4>
                      <p className="text-sm mt-1">{storefront.name}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold">
                        {t('Storefront.publicUrl')}
                      </h4>
                      <a
                        className="text-sm mt-1 flex items-center gap-1 break-all"
                        href={`${SHOP_URL}/shop/${storefront.slug}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {`${SHOP_URL}/shop/${storefront.slug}`}
                        <IonIcon icon={openOutline} className="text-base" />
                      </a>
                    </div>
                  </div>

                  <p className="font-normal text-xs text-gray-500 leading-5 mt-4">
                    {t('Storefront.nameHint')}
                  </p>
                </div>
              ) : (
                hasNoStorefront && (
                  <div className="box p-5 dark:bg-white/5">
                    <h3 className="text-base font-semibold">
                      {t('Storefront.empty.title')}
                    </h3>
                    <p className="font-normal text-sm text-gray-500 leading-6 mt-1">
                      {t('Storefront.empty.subtitle')}
                    </p>
                  </div>
                )
              )}

              {/* Gating on hasNoStorefront rather than on !storefront keeps the
                  form hidden while the storefront is still being read and when
                  that read failed for a reason other than a 404. */}
              {(storefront || hasNoStorefront) && (
                <Formik
                  enableReinitialize={true}
                  initialValues={initialValues}
                  validateOnChange={false}
                  validationSchema={yup.object({
                    tagline: yup
                      .string()
                      .max(
                        STOREFRONT_TAGLINE_MAX_LENGTH,
                        t('FormValidations.storefrontTagline'),
                      ),
                    description: yup
                      .string()
                      .trim()
                      .max(
                        STOREFRONT_DESCRIPTION_MAX_LENGTH,
                        t('FormValidations.storefrontDescription'),
                      )
                      .required(
                        t('FormValidations.storefrontDescriptionRequired'),
                      ),
                    collections: yup
                      .array()
                      .of(
                        yup.object({
                          name: yup
                            .string()
                            .trim()
                            .max(
                              STOREFRONT_COLLECTION_NAME_MAX_LENGTH,
                              t('FormValidations.storefrontCollectionName'),
                            )
                            .required(
                              t(
                                'FormValidations.storefrontCollectionNameRequired',
                              ),
                            ),
                          description: yup
                            .string()
                            .max(
                              STOREFRONT_COLLECTION_DESCRIPTION_MAX_LENGTH,
                              t(
                                'FormValidations.storefrontCollectionDescription',
                              ),
                            ),
                          layout: yup
                            .string()
                            .oneOf(
                              Object.values(StorefrontCollectionLayout),
                              t('FormValidations.storefrontCollectionLayout'),
                            )
                            .required(
                              t('FormValidations.storefrontCollectionLayout'),
                            ),
                          productIds: yup
                            .array()
                            .of(
                              yup
                                .string()
                                .matches(
                                  UUID_REGEX,
                                  t('FormValidations.storefrontProductId'),
                                ),
                            )
                            .max(
                              STOREFRONT_COLLECTION_PRODUCTS_MAX,
                              t('FormValidations.storefrontCollectionProducts'),
                            ),
                        }),
                      )
                      .max(
                        STOREFRONT_COLLECTIONS_MAX,
                        t('FormValidations.storefrontCollections'),
                      ),
                  })}
                  onSubmit={(values) => {
                    submitForm(values);
                  }}
                >
                  {({
                    values,
                    errors,
                    dirty,
                    submitCount,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    resetForm,
                  }) => (
                    <StorefrontForm
                      values={values}
                      errors={errors}
                      dirty={dirty}
                      submitCount={submitCount}
                      productNames={productNames}
                      isCreate={!storefront}
                      isSaving={
                        storefront
                          ? updateStorefrontMutation.isLoading
                          : createStorefrontMutation.isLoading
                      }
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      handleSubmit={handleSubmit}
                      setFieldValue={setFieldValue}
                      resetForm={resetForm}
                    />
                  )}
                </Formik>
              )}
            </div>

            <DeleteStorefrontModal
              targetId={DELETE_MODAL_ID}
              isDeleting={deleteStorefrontMutation.isLoading}
              onConfirm={() => deleteStorefrontMutation.mutate()}
            />

            <ConfirmCollectionRemovalModal
              targetId={REMOVAL_MODAL_ID}
              count={pendingRemovalCount}
              isSaving={updateStorefrontMutation.isLoading}
              onConfirm={() => {
                if (pendingUpdate) {
                  updateStorefrontMutation.mutate(pendingUpdate);
                }
              }}
            />
          </>
        )}
      </div>
    </Layout>
  );
};

const StorefrontForm = ({
  values,
  errors,
  dirty,
  submitCount,
  productNames,
  isCreate,
  isSaving,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  resetForm,
}: {
  values: IStorefrontFormValues;
  errors: FormikErrors<IStorefrontFormValues>;
  dirty: boolean;
  submitCount: number;
  productNames: Record<string, string>;
  // The storefront does not exist yet: this submit creates it rather than
  // saving changes to it.
  isCreate: boolean;
  isSaving: boolean;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  setFieldValue: (field: string, value: any) => void;
  resetForm: () => void;
}) => {
  const { t } = useTranslation();

  // The form is long, so leaving it with unsaved collections should not be silent.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  return (
    <form
      onSubmit={handleSubmit}
      className="text-sm text-black font-medium dark:text-white"
    >
      <div className="box p-5 dark:bg-white/5 mt-6 space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="tagline">{t('Storefront.tagline')}</label>
            <span className="text-xs font-normal text-gray-500">
              {values.tagline.length}/{STOREFRONT_TAGLINE_MAX_LENGTH}
            </span>
          </div>

          <div className="mt-2.5">
            <input
              id="tagline"
              name="tagline"
              type="text"
              className="w-full"
              placeholder={t('Storefront.taglinePlaceholder')}
              value={values.tagline}
              onChange={handleChange}
              onBlur={handleBlur}
            />

            {errors.tagline && (
              <p className="text-red-500 text-xs italic mt-1.5">
                {errors.tagline}
              </p>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="description">{t('Storefront.description')}</label>
            <span className="text-xs font-normal text-gray-500">
              {values.description.length}/{STOREFRONT_DESCRIPTION_MAX_LENGTH}
            </span>
          </div>

          <div className="mt-2.5">
            <textarea
              id="description"
              name="description"
              rows={5}
              className="w-full"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
            />

            {errors.description && (
              <p className="text-red-500 text-xs italic mt-1.5">
                {errors.description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="sm:mb-4 mb-3">
          <h3 className="text-base font-semibold">
            {t('Storefront.collections')}
          </h3>
          <p className="font-normal text-sm text-gray-500 leading-6">
            {t('Storefront.collectionsHint')}
          </p>
        </div>

        <FieldArray name="collections">
          {({ push, remove, swap }) => (
            <div className="space-y-4">
              {values.collections.map((collection, index) => (
                <StorefrontCollectionCard
                  key={collection.key}
                  collection={collection}
                  index={index}
                  total={values.collections.length}
                  errors={
                    ((errors.collections?.[
                      index
                    ] as FormikErrors<IStorefrontCollectionFormValue>) ??
                      {}) as FormikErrors<IStorefrontCollectionFormValue>
                  }
                  submitCount={submitCount}
                  productNames={productNames}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  onMoveUp={() => swap(index, index - 1)}
                  onMoveDown={() => swap(index, index + 1)}
                  onRemove={() => remove(index)}
                />
              ))}

              {typeof errors.collections === 'string' && (
                <p className="text-red-500 text-xs italic mt-1.5">
                  {errors.collections}
                </p>
              )}

              {values.collections.length === 0 && (
                <p className="font-normal text-sm text-gray-500 leading-6">
                  {t('Storefront.noCollections')}
                </p>
              )}

              {values.collections.length < STOREFRONT_COLLECTIONS_MAX && (
                <button
                  type="button"
                  className="cursor-pointer card w-full p-6 border border-dashed dark:border-slate-700"
                  onClick={() => push(createEmptyCollection())}
                >
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <i className="la la-plus-circle text-2xl" />
                    {t('Storefront.addCollection')}
                  </div>
                </button>
              )}
            </div>
          )}
        </FieldArray>
      </div>

      {/* The bar stays opaque and keeps a border and an upward shadow: the form
          scrolls underneath it, and the translucent box background the other
          cards use would let that show through. */}
      <div className="sticky bottom-0 z-10 mt-8 box border1 p-4 shadow-[0_-4px_16px_rgba(15,23,42,0.1)] dark:shadow-[0_-4px_16px_rgba(0,0,0,0.4)] flex items-center justify-between gap-4">
        {isCreate ? (
          <span />
        ) : (
          <span className="text-xs font-normal text-gray-500">
            {dirty ? t('Storefront.unsavedChanges') : t('Storefront.allSaved')}
          </span>
        )}

        <div className="flex items-center gap-3">
          {!isCreate && dirty && (
            <button
              type="button"
              className="button py-2 px-6 text-[14px] border dark:border-gray-600/60 dark:text-slate-50 text-slate-800"
              onClick={() => resetForm()}
            >
              {t('Storefront.discard')}
            </button>
          )}

          <button
            type="submit"
            className="button py-2 px-6 sm:px-12 text-[14px] bg-slate-900 dark:bg-slate-100 text-slate-50 dark:text-slate-800"
            disabled={isSaving}
          >
            {isCreate ? t('Storefront.create') : t('Storefront.save')}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Storefront;
